import { Box, Collapse } from '@mui/material';
import { useStyles } from './ExerciseView.styles';
import { Icons } from '../../../../../../icons';
import { btnType, Button } from '../../../../../atoms/button';
import { Link } from '../../../../../atoms/link';
import { linkType } from '../../../../../atoms/link/Link.types';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAssignedEvaluationState } from '../../../../../../../redux/modules/conversation/selectors';
import { colors } from '../../../../../../colors';
import MedicalFile from '../../../../../../icons/lotties/lottieIcons/MedicalFile';
import FireWorks from '../../../../../../icons/lotties/lottieIcons/FireWorks';
import clsx from 'clsx';
import { formatTheoryName } from '../../../../../../../utils/CommonUtils';

const ExerciseCover = ({
  currentExercise,
  expandedExercise,
  scrollToTop,
  setScrollToTop,
}) => {
  const ref = useRef(null);
  const classes = useStyles();

  useEffect(() => {
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setScrollToTop(false);
    }
  }, [scrollToTop]);

  const getSubtitle = () => {
    let subtitle = '';
    if (currentExercise?.exerciseStats?.mainInstructions > 0) {
      subtitle += `${currentExercise.exerciseStats.mainInstructions} Steps`;
    }
    if (currentExercise?.exerciseStats?.subInstructions > 0) {
      subtitle += ` • ${currentExercise.exerciseStats.subInstructions} Sub Steps`;
    }
    if (subtitle === '') {
      return null;
    }
    return subtitle;
  };

  const subtitle = getSubtitle();

  return (
    <Box className={classes.evaluationsBox} ref={ref}>
      <Box className={classes.welcomeFrame}>
        <Box display="flex" flexDirection="column" flexGrow={1} flex={1} gap="4px">
          <p className={classes.sectionTitle}>{currentExercise?.name}</p>
          {expandedExercise?.meta?.theory && (
            <p className={classes.scriptLabel}>{formatTheoryName(expandedExercise.meta.theory)}</p>
          )}
          <p className={classes.sectionSubtitle}>{subtitle}</p>
          {expandedExercise?.meta?.timeRequired && (
            <p className={classes.sectionSubtitle}>{expandedExercise.meta.timeRequired} mins</p>
          )}
        </Box>
        <MedicalFile className={classes.animatedIcon} />
      </Box>
      {currentExercise?.description && (
        <Box className={classes.descriptionBox}>
          <p className={classes.descriptionText}>{currentExercise.description}</p>
        </Box>
      )}
    </Box>
  );
};

const ExerciseStep = ({
  currentBlock,
  scrollToTop,
  setScrollToTop,
  currentExercise,
  exerciseHelper,
}) => {
  const classes = useStyles();
  const ref = useRef(null);
  const [expandedSteps, setExpandedSteps] = useState(new Set());

  useEffect(() => {
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setScrollToTop(false);
    }
  }, [scrollToTop]);

  const stepNumber = getStepNumber(currentBlock, exerciseHelper);

  const toggleStep = (stepId) => {
    const newExpandedSteps = new Set(expandedSteps);
    if (newExpandedSteps.has(stepId)) {
      newExpandedSteps.delete(stepId);
    } else {
      newExpandedSteps.add(stepId);
    }
    setExpandedSteps(newExpandedSteps);
  };

  // Helper function to render a single step (main or sub)
  const renderStep = (step, number, isMain = false) => {
    const isExpanded = expandedSteps.has(step.cbId);
    const hasScript = step.text || step.basicInfo?.text;
    
    return (
      <Box 
        key={step.cbId} 
        className={classes.exerciseStepContainer}
        sx={{
          gap: '0px !important',
        }}
      >
        {/* Step Name - Clickable Header */}
        <Box 
          className={classes.stepHeader}
          sx={{
            display: 'flex !important',
            flexDirection: 'row !important',
            justifyContent: 'space-between !important',
            alignItems: 'center !important',
            gap: '8px !important',
          }}
        >
          <p className={classes.stepTitle}>
            {number}. {step.name || step.basicInfo?.name}
          </p>
          {hasScript && (
            <Link 
              variant={linkType.CALLBACK} 
              onClick={(e) => {
                e.stopPropagation();
                toggleStep(step.cbId);
              }}
            >
              {isExpanded ? 'Hide script' : 'Show script'}
            </Link>
          )}
        </Box>

        {/* Collapsible Content */}
        {hasScript && (
          <Collapse in={isExpanded}>
            <Box sx={{ paddingTop: '24px' }}>
              <Box className={classes.scriptBox}>
                <Box className={classes.scriptHeader}>
                  <Icons glyph={'chat-outlined'} className={classes.scriptIcon} />
                  <p className={classes.scriptLabel}>Script:</p>
                </Box>
                <Box className={classes.scriptContent}>
                  <p className={classes.scriptText}>{step.text || step.basicInfo?.text}</p>
                </Box>
              </Box>
            </Box>
          </Collapse>
        )}
      </Box>
    );
  };

  // Helper function to recursively render substeps with proper numbering
  const renderSubSteps = (subSteps, parentNumber) => {
    if (!subSteps || subSteps.length === 0) return null;

    return subSteps.map((subStep, index) => {
      const subStepNumber = `${parentNumber}.${index + 1}`;
      
      // Check if this substep has its own children
      const hasChildren = subStep.children && subStep.children.length > 0;
      
      return (
        <Box key={subStep.cbId}>
          {renderStep(subStep, subStepNumber, false)}
          {hasChildren && renderSubSteps(subStep.children, subStepNumber)}
        </Box>
      );
    });
  };

  return (
    <Box className={classes.evaluationsBox} ref={ref}>
      <Box className={classes.welcomeFrame}>
        <Box display="flex" flexDirection="column" flexGrow={1} flex={1} gap="4px">
          <p className={classes.sectionTitle}>{currentExercise?.name}</p>
          <p className={classes.sectionSubtitle}>
            Main Step {stepNumber}
          </p>
        </Box>
        <MedicalFile className={classes.animatedIcon} />
      </Box>

      {/* Render Main Step */}
      {renderStep(currentBlock, stepNumber, true)}

      {/* Render All SubSteps if they exist */}
      {currentBlock.subSteps && currentBlock.subSteps.length > 0 && (
        <Box sx={{ backgroundColor: colors.white }}>
          {currentBlock.subSteps.map((subStep, index) => {
            const subStepNumber = `${stepNumber}.${index + 1}`;
            const hasChildren = subStep.children && subStep.children.length > 0;
            
            return (
              <Box key={subStep.cbId}>
                {renderStep(subStep, subStepNumber, false)}
                {hasChildren && renderSubSteps(subStep.children, subStepNumber)}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

const getStepNumber = (block, exerciseHelper) => {
  if (!exerciseHelper) return '';

  const allSteps = exerciseHelper.getAllSteps();

  // Helper function to build the number hierarchy recursively
  const buildNumber = (currentBlock) => {
    if (!currentBlock.stepId) {
      // This is a main step - find its position among main steps
      const mainSteps = exerciseHelper.getMainSteps();
      const mainIndex = mainSteps.findIndex(step => step.cbId === currentBlock.cbId);
      return `${mainIndex + 1}`;
    } else {
      // This is a substep - count position among siblings with same parent
      const parent = allSteps.find(step => step.cbId === currentBlock.stepId);
      const siblings = allSteps.filter(step => step.stepId === currentBlock.stepId);
      const siblingIndex = siblings.findIndex(step => step.cbId === currentBlock.cbId);
      
      // Recursively build parent's number and append this position
      const parentNumber = buildNumber(parent);
      return `${parentNumber}.${siblingIndex + 1}`;
    }
  };

  return buildNumber(block);
};

const ExerciseRecommendations = ({
  hasExercises,
  recommendations,
  onAddExercise,
  openEvaluationsDrawer,
  allCompleted,
}) => {
  const classes = useStyles();
  return (
    <Box className={classes.recommendationsBox}>
      {!hasExercises && (
        <Box display="flex" flexDirection="column" gap="4px">
          <p className={classes.recommendationsTitle}>
            {allCompleted ? 'Other exercises' : 'Recommended exercises'}
          </p>
          {allCompleted && (
            <p className={classes.recommendationsSubtitle}>Not required, but recommended by us.</p>
          )}
        </Box>
      )}
      <Box className={classes.recommendations}>
        {recommendations.map(exercise => {
          const stats = exercise.exerciseStats;
          return (
            <Box className={classes.card} key={exercise.id}>
              <Box className={classes.evaluationWrapper}>
                <Box className={classes.cardContent}>
                  <p className={classes.cardTitle}>{exercise.name}</p>
                  {stats && (
                    <p className={classes.cardSubtitle}>
                      {stats?.totalInstructions} Steps • {stats?.mainInstructions} Main •{' '}
                      {stats?.subInstructions} Sub
                    </p>
                  )}
                </Box>
              </Box>
              <Button
                variant={btnType.OUTLINE}
                className={classes.useBtn}
                onClick={() => {
                  onAddExercise([exercise.id]);
                }}
              >
                Use
              </Button>
            </Box>
          );
        })}
        {!allCompleted && (
          <Box className={classes.footer}>
            <p className={classes.footerTitle}>
              {hasExercises ? 'Missing something?' : 'Want to pick your own?'}
            </p>
            <Button variant={btnType.TEXT} className={classes.textLink} onClick={openEvaluationsDrawer}>
              Add Exercise
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const ExerciseView = ({
  openEvaluationsDrawer,
  onAddEvaluation,
  currentExercise,
  currentBlock,
  expandedExercise,
  scrollToTop,
  setScrollToTop,
  exerciseHelper,
  exercisesCompleted,
  onRejectEvaluation,
}) => {
  const classes = useStyles();
  const { evaluations } = useSelector(selectAssignedEvaluationState);

  const exercises = evaluations?.filter(ev => ev.type === 'EXERCISE' && ev.status !== 'REJECTED') || [];
  const hasExercises = exercises.length > 0;
  const unassignedExercises = []; // TODO: Get from selector if needed

  return (
    <Box className={classes.container}>
      {exercisesCompleted ? (
        <Box className={classes.evaluationsBox}>
          <Box className={clsx(classes.titleBox, classes.bgWhite)}>
            <FireWorks className={classes.animatedIcon} />
            <Box display="flex" flexDirection="column" gap="4px" alignItems="center">
              <p className={classes.sectionTitle}>Nice job!</p>
              <p className={clsx([classes.textAlignCenter, classes.sectionSubtitle])}>
                You've completed all assigned exercises!
              </p>
            </Box>
          </Box>
          <ExerciseRecommendations
            allCompleted
            recommendations={unassignedExercises.slice(0, 3)}
            onAddExercise={onAddEvaluation}
            openEvaluationsDrawer={openEvaluationsDrawer}
          />
        </Box>
      ) : (
        <>
          {!currentExercise && (
            <Box className={classes.evaluationsBox}>
              <Box className={classes.welcomeFrame}>
                <Box display="flex" flexDirection="column" flexGrow={1} flex={1} gap="4px">
                  <p className={classes.sectionTitle}>{hasExercises ? 'Exercises' : 'Whoops!'}</p>
                  <p className={classes.sectionSubtitle}>
                    {hasExercises ? (
                      <>
                        <span>Here are the exercises to complete.</span>
                        <span>Ensure they're relevant.</span>
                      </>
                    ) : (
                      `Looks like there are no assigned exercises`
                    )}
                  </p>
                </Box>
                <MedicalFile className={classes.animatedIcon} />
              </Box>
              {!hasExercises && (
                <ExerciseRecommendations
                  recommendations={[]}
                  hasExercises={hasExercises}
                  onAddExercise={onAddEvaluation}
                  onRejectExercise={onRejectEvaluation}
                  openEvaluationsDrawer={openEvaluationsDrawer}
                />
              )}
            </Box>
          )}
          {currentExercise && !currentBlock && (
            <ExerciseCover
              currentExercise={currentExercise}
              expandedExercise={expandedExercise}
              scrollToTop={scrollToTop}
              setScrollToTop={setScrollToTop}
            />
          )}
          {currentBlock && (
            <ExerciseStep
              currentBlock={currentBlock}
              scrollToTop={scrollToTop}
              setScrollToTop={setScrollToTop}
              currentExercise={currentExercise}
              exerciseHelper={exerciseHelper}
            />
          )}
        </>
      )}
    </Box>
  );
};

export { ExerciseView };
